/*
 * Google Maps Tile API — session token manager.
 *
 * Tokens are stored in memory only and never written to localStorage,
 * sessionStorage, or IndexedDB, complying with:
 * https://developers.google.com/maps/documentation/tile/policies#pre-fetching,-caching,-or-storage-of-content
 */

/* global __GOOGLE_MAPS_KEY__ */

const MIN_LIFETIME_MS  = 3_600_000  // enforce at least 1 hour regardless of server header
const REFRESH_BEFORE_MS = 300_000   // refresh 5 min before expiry

let currentToken = null
let expiresAt    = 0
let refreshTimer = null
let pendingFetch = null

function scheduleRefresh(expiryMs) {
  if (refreshTimer) clearTimeout(refreshTimer)
  const delay = Math.max(expiryMs - Date.now() - REFRESH_BEFORE_MS, MIN_LIFETIME_MS * 0.5)
  refreshTimer = setTimeout(fetchNewSession, delay)
}

async function fetchNewSession() {
  if (pendingFetch) return pendingFetch

  pendingFetch = fetch(
    `https://tile.googleapis.com/v1/createSession?key=${__GOOGLE_MAPS_KEY__}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // mapType 'satellite' + layerTypes ['layerRoadmap'] yields hybrid tiles
      // (satellite imagery with road + label overlay baked in). This is the
      // modern Map Tiles API equivalent of the legacy `lyrs=y` parameter — the
      // layer set is fixed at session creation, not per individual tile request.
      body: JSON.stringify({
        mapType: 'satellite',
        layerTypes: ['layerRoadmap'],
        language: 'en-US',
        region: 'US',
      }),
    }
  )
    .then(async res => {
      if (!res.ok) throw new Error(`createSession HTTP ${res.status}`)
      const data = await res.json()

      // Use the expiry timestamp from the response body when available.
      // Fall back to Cache-Control max-age, then default to 1 hour.
      // Always enforce a minimum of MIN_LIFETIME_MS so a zero/missing value
      // cannot cause an immediate re-fetch loop.
      let expiryMs
      if (data.expiry) {
        expiryMs = parseInt(data.expiry, 10) * 1000
      } else {
        const cc = res.headers.get('Cache-Control') || ''
        const maxAgeMatch = cc.match(/max-age=(\d+)/)
        const maxAgeMs = maxAgeMatch ? parseInt(maxAgeMatch[1], 10) * 1000 : 0
        expiryMs = Date.now() + Math.max(maxAgeMs, MIN_LIFETIME_MS)
      }

      // Enforce minimum lifetime so the token is never immediately expired
      expiryMs = Math.max(expiryMs, Date.now() + MIN_LIFETIME_MS)

      currentToken = data.session
      expiresAt    = expiryMs

      scheduleRefresh(expiryMs)
      return currentToken
    })
    .catch(err => {
      console.error('[GoogleTileSession] failed to create session:', err)
      currentToken = null
      // Retry after 30 seconds on error instead of immediately
      if (refreshTimer) clearTimeout(refreshTimer)
      refreshTimer = setTimeout(fetchNewSession, 30_000)
      return null
    })
    .finally(() => { pendingFetch = null })

  return pendingFetch
}

/**
 * Ensures a valid session token exists, fetching / awaiting one if necessary.
 * Concurrent callers share a single in-flight request.
 */
export async function ensureSession() {
  if (currentToken && Date.now() < expiresAt) return currentToken
  return fetchNewSession()
}

/**
 * Returns the current token synchronously, or null if none is available yet.
 * Used inside MapLibre's synchronous transformRequest callback.
 */
export function getCurrentToken() {
  return (currentToken && Date.now() < expiresAt) ? currentToken : null
}

/** Clears the in-memory token and cancels the auto-refresh timer. */
export function clearSession() {
  if (refreshTimer) clearTimeout(refreshTimer)
  refreshTimer = null
  currentToken  = null
  expiresAt     = 0
}
